export type ToastType = "success" | "error" | "info" | "warning";

export type ToastItem = {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration: number;
};

type ToastInput = {
  type?: ToastType;
  title: string;
  description?: string;
  duration?: number;
  key?: string;
};

type Listener = (toasts: ToastItem[]) => void;

const MAX_TOASTS = 4;
const DEFAULT_DURATION = 3200;

let toasts: ToastItem[] = [];
const listeners = new Set<Listener>();
const timers = new Map<string, number>();

function emit() {
  listeners.forEach((listener) => listener(toasts));
}

export function subscribeToasts(listener: Listener) {
  listeners.add(listener);
  listener(toasts);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissToast(id: string) {
  const timer = timers.get(id);
  if (timer) {
    window.clearTimeout(timer);
    timers.delete(id);
  }
  const next = toasts.filter((item) => item.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

export function showToast(input: ToastInput) {
  const id = input.key || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const duration = input.duration ?? DEFAULT_DURATION;
  const item: ToastItem = {
    id,
    type: input.type || "info",
    title: input.title,
    description: input.description,
    duration,
  };

  toasts = [item, ...toasts.filter((existing) => existing.id !== id)].slice(0, MAX_TOASTS);
  emit();

  if (duration > 0) {
    const prev = timers.get(id);
    if (prev) window.clearTimeout(prev);
    timers.set(id, window.setTimeout(() => dismissToast(id), duration));
  }

  return id;
}

export const toast = {
  success: (title: string, description?: string, duration?: number) =>
    showToast({ type: "success", title, description, duration }),
  error: (title: string, description?: string, duration?: number) =>
    showToast({ type: "error", title, description, duration }),
  info: (title: string, description?: string, duration?: number) =>
    showToast({ type: "info", title, description, duration }),
  warning: (title: string, description?: string, duration?: number) =>
    showToast({ type: "warning", title, description, duration }),
  dismiss: dismissToast,
};
